import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import PaymentsIcon from '@mui/icons-material/Payments'
import { useTranslation } from 'react-i18next'

import { adminApi, opsApi } from '../../api/endpoints'
import { useFormatters } from '../../hooks/useFormatters'
import { EmptyState, ErrorState } from '../../components/common/States'
import type { BookingResponse, PaymentMethod } from '../../types/api'

const METHODS: PaymentMethod[] = ['CASH', 'CARD', 'BANK_TRANSFER', 'MOBILEPAY', 'OTHER']

/**
 * The departure-day conversation, on a screen.
 *
 * Nothing is charged here and no card is taken: the guest pays the owner in person, and this
 * records that it happened. The bill itself is derived from the booking and its delivered
 * orders every time it is opened, so it cannot drift from what the guest actually had.
 */
export function AdminBillingPage() {
  const { t } = useTranslation()
  const { dateShort, moneyPrecise, dateTimeAtEstate } = useFormatters()
  const queryClient = useQueryClient()

  const [selected, setSelected] = useState<BookingResponse | null>(null)
  const [amount, setAmount] = useState('')
  const [method, setMethod] = useState<PaymentMethod>('CASH')
  const [note, setNote] = useState('')

  const bookings = useQuery({
    queryKey: ['admin', 'bookings', 'CONFIRMED', 0],
    queryFn: () => adminApi.bookings({ status: 'CONFIRMED', page: 0, size: 50 }),
  })

  const balance = useQuery({
    queryKey: ['admin', 'balance', selected?.id],
    queryFn: () => opsApi.balance(selected!.id),
    enabled: selected !== null,
  })

  const recordPayment = useMutation({
    mutationFn: () =>
      opsApi.recordPayment(selected!.id, {
        amount: Number(amount),
        method,
        note: note || undefined,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'balance'] })
      void queryClient.invalidateQueries({ queryKey: ['admin', 'ops', 'dashboard'] })
      setAmount('')
      setNote('')
    },
  })

  const removePayment = useMutation({
    mutationFn: (paymentId: string) => opsApi.deletePayment(paymentId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'balance'] })
      void queryClient.invalidateQueries({ queryKey: ['admin', 'ops', 'dashboard'] })
    },
  })

  const bill = balance.data
  const amountValue = Number(amount)
  const amountIsValid = amount.trim() !== '' && Number.isFinite(amountValue) && amountValue !== 0

  return (
    <Box>
      <Typography variant="h2" sx={{ mb: 1 }}>
        {t('admin.billing')}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {t('admin.billingIntro')}
      </Typography>

      <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', lg: '340px 1fr' } }}>
        <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 3 }}>
          <Typography variant="h5" sx={{ mb: 1.5 }}>
            {t('booking.statusCONFIRMED')}
          </Typography>

          {bookings.isError && <ErrorState error={bookings.error} />}
          {bookings.data?.content.length === 0 && (
            <EmptyState icon="🌿" title={t('admin.noPending')} />
          )}

          <Stack divider={<Divider />}>
            {(bookings.data?.content ?? []).map((booking) => (
              <Box
                key={booking.id}
                component="button"
                type="button"
                onClick={() => setSelected(booking)}
                sx={{
                  textAlign: 'start',
                  border: 0,
                  bgcolor: selected?.id === booking.id ? 'action.selected' : 'transparent',
                  cursor: 'pointer',
                  py: 1.25,
                  px: 1,
                  borderRadius: 2,
                  font: 'inherit',
                  color: 'inherit',
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {booking.guestName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {booking.unitName} · {dateShort(booking.checkIn)} → {dateShort(booking.checkOut)}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Paper>

        <Box>
          {!selected && <EmptyState icon="🧾" title={t('admin.pickBooking')} />}

          {selected && balance.isError && <ErrorState error={balance.error} />}

          {selected && bill && (
            <Stack spacing={3}>
              <Paper elevation={0} sx={{ p: 2.5, border: 1, borderColor: 'divider', borderRadius: 3 }}>
                <Typography variant="h5" sx={{ mb: 0.5 }}>
                  {bill.guestName} · {bill.unitName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {bill.bookingReference}
                </Typography>

                <Stack spacing={1} sx={{ mt: 2 }}>
                  <Line label={t('booking.accommodation')} value={moneyPrecise(bill.accommodationTotal)} />
                  <Line label={t('booking.cleaningFee')} value={moneyPrecise(bill.cleaningFee)} />
                  <Line label={t('admin.extras')} value={moneyPrecise(bill.servicesTotal)} />
                  <Divider />
                  <Line label={t('booking.total')} value={moneyPrecise(bill.grandTotal)} strong />
                  <Line label={t('admin.paid')} value={moneyPrecise(bill.paid)} />
                  <Line
                    label={t('admin.balanceDue')}
                    value={moneyPrecise(bill.balanceDue)}
                    strong
                    highlight={bill.balanceDue > 0}
                  />
                </Stack>

                {bill.billableOrders.length > 0 && (
                  <>
                    <Typography variant="subtitle2" sx={{ mt: 2.5, mb: 1 }}>
                      {t('admin.extras')}
                    </Typography>
                    <Stack spacing={0.5}>
                      {bill.billableOrders.map((order) => (
                        <Stack
                          key={order.id}
                          direction="row"
                          justifyContent="space-between"
                          spacing={2}
                        >
                          <Typography variant="body2" color="text.secondary">
                            {dateTimeAtEstate(order.requestedFor)} ·{' '}
                            {order.lines.map((line) => `${line.quantity}× ${line.name}`).join(', ')}
                          </Typography>
                          <Typography variant="body2">{moneyPrecise(order.totalAmount)}</Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </>
                )}
              </Paper>

              <Paper elevation={0} sx={{ p: 2.5, border: 1, borderColor: 'divider', borderRadius: 3 }}>
                <Typography variant="h5" sx={{ mb: 2 }}>
                  {t('admin.recordPayment')}
                </Typography>

                {recordPayment.isError && <ErrorState error={recordPayment.error} />}

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                  <TextField
                    label={t('admin.amount')}
                    value={amount}
                    onChange={(event) => setAmount(event.target.value)}
                    type="number"
                    slotProps={{
                      input: {
                        endAdornment: <InputAdornment position="end">€</InputAdornment>,
                      },
                    }}
                    sx={{ maxWidth: { sm: 180 } }}
                  />
                  <TextField
                    select
                    label={t('admin.method')}
                    value={method}
                    onChange={(event) => setMethod(event.target.value as PaymentMethod)}
                    sx={{ minWidth: 180 }}
                  >
                    {METHODS.map((option) => (
                      <MenuItem key={option} value={option}>
                        {t(`payment.method${option}`)}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label={t('admin.noteOptional')}
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                    fullWidth
                  />
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                  <Button
                    variant="contained"
                    startIcon={<PaymentsIcon />}
                    disabled={!amountIsValid || recordPayment.isPending}
                    onClick={() => recordPayment.mutate()}
                  >
                    {t('admin.recordPayment')}
                  </Button>
                  {bill.balanceDue > 0 && (
                    <Button size="small" onClick={() => setAmount(String(bill.balanceDue))}>
                      {t('admin.payFull', { amount: moneyPrecise(bill.balanceDue) })}
                    </Button>
                  )}
                  {/* A refund is a payment with the sign turned round, which is why the field
                      accepts a negative number rather than hiding a separate "refund" flow. */}
                  <Typography variant="caption" color="text.secondary">
                    {t('admin.refundHint')}
                  </Typography>
                </Stack>

                {bill.payments.length > 0 && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Stack spacing={1}>
                      {bill.payments.map((payment) => (
                        <Stack
                          key={payment.id}
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          spacing={2}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2">
                              {moneyPrecise(payment.amount)} ·{' '}
                              {t(`payment.method${payment.method}`)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {dateTimeAtEstate(payment.paidAt)}
                              {payment.recordedBy ? ` · ${payment.recordedBy}` : ''}
                              {payment.note ? ` · ${payment.note}` : ''}
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            aria-label={t('common.delete')}
                            onClick={() => removePayment.mutate(payment.id)}
                            disabled={removePayment.isPending}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      ))}
                    </Stack>
                  </>
                )}
              </Paper>
            </Stack>
          )}
        </Box>
      </Box>
    </Box>
  )
}

function Line({
  label,
  value,
  strong,
  highlight,
}: {
  label: string
  value: string
  strong?: boolean
  highlight?: boolean
}) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2}>
      <Typography variant="body2" sx={{ fontWeight: strong ? 700 : 400 }}>
        {label}
      </Typography>
      {highlight ? (
        <Chip size="small" color="warning" label={value} />
      ) : (
        <Typography variant="body2" sx={{ fontWeight: strong ? 700 : 400 }}>
          {value}
        </Typography>
      )}
    </Stack>
  )
}
